import React, { useEffect, useState, useCallback } from "react";
import { api, hasAnyRole } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Megaphone, Download, Settings2, Clapperboard, Send } from "lucide-react";

const STATUSES = [
  { key: "planned", label: "Planned" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
];

const KINDS = [
  { key: "production", label: "Production", icon: Clapperboard, badge: "PROD", badgeCls: "bg-amber-500/20 text-amber-400" },
  { key: "posting", label: "Posting", icon: Send, badge: "POST", badgeCls: "bg-blue-500/20 text-blue-400" },
];

const channelsOf = (p) => p.channels || (p.channel ? [p.channel] : []);

export default function MarketingPage() {
  const { user } = useAuth();
  const canEdit = hasAnyRole(user, ["admin", "operations"]);
  const [plans, setPlans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [editing, setEditing] = useState(null); // plan object or null
  const [presetDate, setPresetDate] = useState("");

  const load = useCallback(() => {
    api.get("/marketing/plans").then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
    api.get("/marketing/channels").then((r) => setChannels(r.data || [])).catch(() => setChannels([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const channelOf = (name) => channels.find((c) => c.name === name) || { name, color: "#a1a1aa" };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  // A plan shows on every day between its start date and (optional) end date
  const plansOnDay = (d) => {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return plans.filter((p) => {
      const from = (p.date || "").slice(0, 10);
      const to = (p.date_to || "").slice(0, 10) || from;
      return from && from <= iso && iso <= to;
    });
  };

  const monthName = first.toLocaleString("en-MY", { month: "long", year: "numeric" });
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;

  const downloadPlans = async (fmt) => {
    try {
      const r = await api.get(`/marketing/plans/export`, { params: { month: monthKey, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marketing-plan-${monthKey}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(fmt === "ics" ? "Calendar file downloaded — import it into Google/Apple Calendar" : "CSV downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const openCreate = (date) => {
    if (!canEdit) return;
    setEditing(null);
    setPresetDate(date || "");
    setDialogOpen(true);
  };
  const openEdit = (plan) => {
    setEditing(plan);
    setPresetDate("");
    setDialogOpen(true);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="marketing-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><Megaphone className="w-3 h-3" /> Marketing Plan</div>
          <h1 className="font-display text-4xl mt-2">{monthName}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="mkt-cal-prev" className="p-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-2 text-sm rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="mkt-cal-next" className="p-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => downloadPlans("ics")} data-testid="mkt-download-ics" title="Download this month as a calendar file (Google/Apple Calendar)" className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Download className="w-3.5 h-3.5" /> Calendar
          </button>
          <button onClick={() => downloadPlans("csv")} data-testid="mkt-download-csv" title="Download this month's tasks as CSV (Excel)" className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          {canEdit && (
            <Button onClick={() => openCreate("")} data-testid="new-plan-btn" className="ml-2 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> New Plan
            </Button>
          )}
        </div>
      </div>

      {/* Channel legend */}
      <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="channel-legend">
        {channels.map((c) => (
          <span key={c.id} className="flex items-center gap-1.5 text-[11px] text-zinc-400 capitalize">
            <span className="w-2 h-2 rounded-full" style={{ background: c.color }} /> {c.name}
          </span>
        ))}
        {canEdit && (
          <button onClick={() => setManageOpen(true)} data-testid="mkt-manage-channels" className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[#D4AF37] border border-[#2a2a2a] hover:border-[#D4AF37] rounded-sm px-2 py-1">
            <Settings2 className="w-3 h-3" /> Manage channels
          </button>
        )}
      </div>

      <div className="grid grid-cols-7 gap-px bg-[#1a1a1a] border border-[#1a1a1a] rounded-sm overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-[#0a0a0a] py-2 text-center label-eyebrow text-[10px]">{d}</div>
        ))}
        {cells.map((c, i) => {
          if (!c) return <div key={`empty-${i}`} className="bg-[#050505] min-h-[96px]" />;
          const dayPlans = plansOnDay(c);
          const isToday = c.toDateString() === new Date().toDateString();
          const iso = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}-${String(c.getDate()).padStart(2, "0")}`;
          return (
            <div
              key={iso}
              onClick={() => openCreate(iso)}
              data-testid={`mkt-day-${iso}`}
              className={`bg-[#0a0a0a] min-h-[96px] p-2 space-y-1 ${isToday ? "ring-1 ring-[#D4AF37]" : ""} ${canEdit ? "cursor-pointer hover:bg-[#101010]" : ""}`}
            >
              <div className={`text-xs ${isToday ? "text-[#D4AF37] font-medium" : "text-zinc-500"}`}>{c.getDate()}</div>
              {dayPlans.slice(0, 3).map((p) => {
                const chs = channelsOf(p);
                const kind = KINDS.find((k) => k.key === (p.kind || "posting")) || KINDS[1];
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                    data-testid={`plan-chip-${p.id}`}
                    title={`${kind.label}${p.time ? ` · ${p.time}` : ""} · ${chs.map((k) => channelOf(k).name).join(" + ")} · ${p.title}`}
                    className={`w-full text-left text-[11px] px-1.5 py-0.5 rounded-sm bg-[#1a1a1a] hover:bg-[#242424] truncate transition-colors flex items-center gap-1 ${p.status === "done" ? "opacity-50 line-through" : ""}`}
                  >
                    <span className="flex items-center gap-0.5 shrink-0">
                      {chs.slice(0, 3).map((k) => (
                        <span key={k} className="w-1.5 h-1.5 rounded-full" style={{ background: channelOf(k).color }} />
                      ))}
                    </span>
                    <span className={`shrink-0 text-[8px] px-1 rounded-sm ${kind.badgeCls}`}>{kind.badge}</span>
                    {p.time && <span className="shrink-0 text-[9px] text-zinc-500">{p.time}</span>}
                    <span className="truncate">{p.title}</span>
                  </button>
                );
              })}
              {dayPlans.length > 3 && <div className="text-[10px] text-zinc-500">+{dayPlans.length - 3} more</div>}
            </div>
          );
        })}
      </div>

      <PlanDialog
        open={dialogOpen}
        editing={editing}
        presetDate={presetDate}
        canEdit={canEdit}
        channels={channels}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); load(); }}
      />
      {manageOpen && (
        <ChannelManager channels={channels} onClose={() => setManageOpen(false)} onChanged={load} />
      )}
    </div>
  );
}

function ChannelManager({ channels, onClose, onChanged }) {
  const [rows, setRows] = useState(channels.map((c) => ({ ...c })));
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#D4AF37");

  const saveRow = async (r) => {
    try {
      await api.patch(`/marketing/channels/${r.id}`, { name: r.name, color: r.color });
      toast.success("Channel updated — plans renamed too");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delRow = async (r) => {
    if (!window.confirm(`Delete channel "${r.name}"? Channels still used by plans can't be deleted.`)) return;
    try {
      await api.delete(`/marketing/channels/${r.id}`);
      setRows((rs) => rs.filter((x) => x.id !== r.id));
      toast.success("Channel deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const addRow = async () => {
    if (!newName.trim()) return;
    try {
      const r = await api.post("/marketing/channels", { name: newName.trim(), color: newColor });
      setRows((rs) => [...rs, r.data]);
      setNewName("");
      toast.success("Channel added");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md" data-testid="channel-manager">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage Channels</DialogTitle>
          <DialogDescription className="sr-only">Add, rename, recolor or delete marketing channels.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {rows.map((r, i) => (
            <div key={r.id} className="flex items-center gap-2" data-testid={`channel-row-${r.name}`}>
              <input type="color" value={r.color || "#a1a1aa"} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, color: e.target.value } : x))}
                className="w-8 h-8 rounded-sm border border-[#2a2a2a] bg-transparent cursor-pointer" data-testid={`channel-color-${r.name}`} />
              <Input value={r.name} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                className="flex-1 h-9 bg-[#050505] border-[#2a2a2a] text-sm" data-testid={`channel-name-${r.id}`} />
              <Button onClick={() => saveRow(r)} data-testid={`channel-save-${r.id}`} className="h-9 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm text-xs px-3">Save</Button>
              <button onClick={() => delRow(r)} data-testid={`channel-delete-${r.id}`} className="p-2 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-[#1a1a1a]">
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-8 h-8 rounded-sm border border-[#2a2a2a] bg-transparent cursor-pointer" data-testid="channel-new-color" />
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New channel name — e.g. xiaohongshu" data-testid="channel-new-name" className="flex-1 h-9 bg-[#050505] border-[#2a2a2a] text-sm" />
          <Button onClick={addRow} disabled={!newName.trim()} data-testid="channel-add" className="h-9 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm text-xs px-3"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanDialog({ open, editing, presetDate, canEdit, channels, onClose, onSaved }) {
  const blank = { title: "", date: "", date_to: "", time: "", kind: "posting", channels: [], status: "planned", notes: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || "",
        date: (editing.date || "").slice(0, 10),
        date_to: (editing.date_to || "").slice(0, 10),
        time: editing.time || "",
        kind: editing.kind || "posting",
        channels: channelsOf(editing),
        status: editing.status || "planned",
        notes: editing.notes || "",
      });
    } else {
      setForm({ ...blank, date: presetDate || "", channels: channels[0] ? [channels[0].name] : [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, presetDate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (name) => {
    if (!canEdit) return;
    setForm((f) => {
      const has = f.channels.includes(name);
      return { ...f, channels: has ? f.channels.filter((c) => c !== name) : [...f.channels, name] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.channels.length === 0) { toast.error("Pick at least one channel"); return; }
    if (form.date_to && form.date_to < form.date) { toast.error("End date cannot be before start date"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/marketing/plans/${editing.id}`, form);
        toast.success("Plan updated");
      } else {
        await api.post("/marketing/plans", form);
        toast.success("Plan created");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete plan "${editing.title}"?`)) return;
    try {
      await api.delete(`/marketing/plans/${editing.id}`);
      toast.success("Plan deleted");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? (canEdit ? "Edit Plan" : "Plan Details") : "New Marketing Plan"}</DialogTitle>
          <DialogDescription className="sr-only">Marketing plan details: title, dates, time, type, channel, status and notes.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Title</Label>
            <Input required disabled={!canEdit} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Raya promo post" data-testid="plan-title" className="bg-[#050505] border-[#2a2a2a]" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Type</Label>
            <div className="flex gap-1.5" data-testid="plan-kind">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const on = form.kind === k.key;
                return (
                  <button key={k.key} type="button" disabled={!canEdit} onClick={() => set("kind", k.key)} data-testid={`plan-kind-${k.key}`}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm border transition-colors ${on ? "bg-[#D4AF37] text-black border-[#D4AF37] font-medium" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>
                    <Icon className="w-3.5 h-3.5" /> {k.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[10px]">Date From</Label>
              <Input type="date" required disabled={!canEdit} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="plan-date" className="bg-[#050505] border-[#2a2a2a]" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[10px]">Date To (optional)</Label>
              <Input type="date" disabled={!canEdit} value={form.date_to} min={form.date} onChange={(e) => set("date_to", e.target.value)} data-testid="plan-date-to" className="bg-[#050505] border-[#2a2a2a]" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[10px]">Time</Label>
              <Input type="time" disabled={!canEdit} value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="plan-time" className="bg-[#050505] border-[#2a2a2a]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Status</Label>
            <select disabled={!canEdit} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="plan-status" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Channels (pick one or more)</Label>
            <div className="flex flex-wrap gap-2" data-testid="plan-channels">
              {channels.map((c) => {
                const on = form.channels.includes(c.name);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.name)}
                    data-testid={`plan-channel-${c.name}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs capitalize transition-colors ${on ? "border-transparent text-black font-medium" : "border-[#2a2a2a] text-zinc-400 hover:border-zinc-500"} ${!canEdit ? "cursor-default" : ""}`}
                    style={on ? { background: c.color } : {}}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? "rgba(0,0,0,0.5)" : c.color }} />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[10px]">Notes</Label>
            <textarea disabled={!canEdit} value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="plan-notes" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm p-2 text-sm" placeholder="Caption ideas, budget, links…" />
          </div>
          <div className="flex justify-between gap-2 pt-3 border-t border-[#1a1a1a]">
            <div>
              {canEdit && editing && (
                <Button type="button" variant="outline" onClick={remove} data-testid="plan-delete-btn" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-300 rounded-sm">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="border-[#2a2a2a]">Close</Button>
              {canEdit && (
                <Button type="submit" disabled={saving} data-testid="plan-save-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
                  {saving ? "Saving…" : (editing ? "Save Changes" : "Create Plan")}
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
